import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import CalendarIcon from "../assets/icons/Planner.png";
import DealIcon from "../assets/icons/Discount.png";
import SpinAndWinIcon from "../assets/icons/Community ticket.png";
import Icon from "./Icon";
import useUnreadMessages from "../hooks/useUnreadMessages";
import usePromoterStatus from "../hooks/usePromoterStatus";
import { supabase } from "../supabaseClient";

export default function MobileMenu() {
  const location = useLocation();
  const path = location.pathname.split("/").filter(Boolean)[0] || "";
  const isHousematePage = location.pathname.startsWith("/housemates");
  const user = useSelector(selectUser);
  const { unreadCount } = useUnreadMessages();
  const { isPromoter, copyRefLink } = usePromoterStatus();
  const [copied, setCopied] = useState(false);
  const [isUGC, setIsUGC] = useState(false);
  const isLoggedIn = !!(user.id || user.token || user.promoterRef);

  useEffect(() => {
    const checkUGC = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user?.id) { setIsUGC(false); return; }
        const authId = auth.user.id;

        const { data: dbUser } = await supabase.from("User")
          .select("id").eq("auth_user_id", authId).maybeSingle();
        const numericUserId = dbUser?.id;

        let data = null;
        if (numericUserId != null) {
          ({ data } = await supabase.from("ambassadors")
            .select("id").eq("user_id", numericUserId)
            .eq("is_ugc_partner", true).maybeSingle());
        }

        if (!data && auth.user.email) {
          const { data: emailMatch } = await supabase.from("ambassadors")
            .select("id").ilike("ugc_email", auth.user.email.trim())
            .eq("is_ugc_partner", true).maybeSingle();
          setIsUGC(!!emailMatch);
          return;
        }

        setIsUGC(!!data);
      } catch (e) { setIsUGC(false); }
    };
    checkUGC();
  }, [user?.id]);

  // Token-only users (no Supabase session) can't use Inbox
  const isTokenOnly = !!(user.token && !user.id);

  const handlePromoterShare = async () => {
    const success = await copyRefLink();
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const options = [
    {
      id: "events",
      icon: CalendarIcon,
      name: "Events",
      link: "/events",
      activeLink: "events",
    },
    {
      id: "spin-and-win",
      icon: SpinAndWinIcon,
      name: "Win tickets",
      link: "/spin-and-win",
      activeLink: "spin-and-win",
    },
    isTokenOnly
      ? {
          id: "deals",
          icon: DealIcon,
          name: "Deals",
          link: "/lifestyle",
          activeLink: "lifestyle",
        }
      : {
          id: "inbox",
          iconComponent: "inbox",
          name: "Inbox",
          link: "/inbox",
          activeLink: "inbox",
          badge: unreadCount,
        },
    // Hide restaurants until live with active restaurants
    // {
    //   id: "restaurants",
    //   iconComponent: <Icon icon="mdi:silverware-fork-knife" className="w-[22px] h-[22px]" />,
    //   name: "Restaurants",
    //   link: "/restaurants",
    //   activeLink: "restaurants",
    // },
    ...(isLoggedIn
      ? [{
          id: "housemates",
          iconComponent: "home",
          name: "Housemates",
          link: "/housemates",
          activeLink: "housemates",
        }]
      : []),
    ...(isUGC
      ? [{
          id: "ugc",
          iconComponent: "video-camera",
          name: "UGC",
          link: "/profile/ugc",
          activeLink: "ugc",
        }]
      : []),
  ];

  const isActive = (activeLink) => path === activeLink;

  return (
    <div
      className="fixed bottom-0 z-[100] w-full bg-[#0B0415]/95 backdrop-blur-xl border-t border-white/[0.06]"
      data-mobile-tab-bar
    >
      {/* Safe area + content */}
      <div className="flex items-end justify-around px-2 pt-2 pb-[env(safe-area-inset-bottom,8px)]">
        {options.map((option, index) => {
          const active = isActive(option.activeLink);

          return (
            <React.Fragment key={option.id}>
              <Link
                to={option.link}
                className="group relative flex flex-col items-center justify-center min-w-[64px] py-1 gap-[5px] transition-all duration-200"
              >
                {/* Active indicator line */}
                <span
                  className={`absolute -top-2 left-1/2 -translate-x-1/2 h-[2.5px] rounded-full transition-all duration-300 ease-out ${
                    active
                      ? "w-[28px] bg-[#AD7AEF] shadow-[0_0_8px_rgba(173,122,239,0.6)]"
                      : "w-0 bg-transparent"
                  }`}
                />

                {/* Icon */}
                <span className={`relative transition-transform duration-200 ${active ? "scale-105" : "group-active:scale-90"}`}>
                  {option.iconComponent ? (
                    <>
                      <Icon
                        icon={
                          option.iconComponent === "inbox" ? "heroicons:chat-bubble-left-right" :
                          option.iconComponent === "home" ? "lucide:home" :
                          option.iconComponent === "video-camera" ? "heroicons:video-camera" :
                          "heroicons:chat-bubble-left-right"
                        }
                        className={`w-[23px] h-[23px] transition-colors duration-200 ${
                          active ? "text-[#AD7AEF]" : "text-neutral-400"
                        }`}
                      />
                      {option.badge > 0 && (
                        <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] bg-red-500 rounded-full border-[1.5px] border-[#0B0415] flex items-center justify-center px-[3px]">
                          <span className="text-[9px] font-bold text-white leading-none">
                            {option.badge > 9 ? "9+" : option.badge}
                          </span>
                        </span>
                      )}
                    </>
                  ) : (
                    <img
                      src={option.icon}
                      alt={option.name}
                      className="w-[23px] h-[23px] transition-all duration-200"
                      style={{
                        filter: active
                          ? "brightness(0) saturate(100%) invert(67%) sepia(37%) saturate(7096%) hue-rotate(226deg) brightness(102%) contrast(87%)"
                          : "brightness(0.65) invert(0.7)",
                        opacity: active ? 1 : 0.7,
                      }}
                    />
                  )}
                </span>

                {/* Label */}
                <span
                  className={`text-[10px] leading-none font-semibold tracking-wide transition-colors duration-200 ${
                    active ? "text-[#AD7AEF]" : "text-neutral-500"
                  }`}
                >
                  {option.name}
                </span>
              </Link>

              {/* Center "+" button — only on housemates page, between 2nd and 3rd item */}
              {index === 1 && isHousematePage && (
                <Link
                  to="/housemates/create"
                  className="relative -mt-[36px] mx-[2px] flex-shrink-0"
                >
                  <div
                    className="w-[52px] h-[52px] rounded-full flex items-center justify-center border-[1.5px] border-[#AD7AEF]/40 shadow-[0_4px_24px_rgba(119,33,229,0.45)] active:scale-90 transition-all duration-200"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(119, 33, 229, 1) 0%, rgba(85, 2, 191, 1) 100%)",
                    }}
                  >
                    <Icon
                      icon="heroicons:plus"
                      className="w-[26px] h-[26px] text-white stroke-[2]"
                    />
                  </div>
                </Link>
              )}

              {/* Center megaphone button — for promoters, between 2nd and 3rd item (not on housemates pages) */}
              {index === 1 && isPromoter && !isHousematePage && (
                <button
                  onClick={handlePromoterShare}
                  className="relative -mt-[36px] mx-[2px] flex-shrink-0"
                >
                  <div
                    className="w-[52px] h-[52px] rounded-full flex items-center justify-center border-[1.5px] border-[#AD7AEF]/40 shadow-[0_4px_24px_rgba(119,33,229,0.45)] active:scale-90 transition-all duration-200"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(119, 33, 229, 1) 0%, rgba(85, 2, 191, 1) 100%)",
                    }}
                  >
                    <Icon
                      icon={copied ? "heroicons:check" : "heroicons:megaphone"}
                      className={`w-[22px] h-[22px] text-white transition-transform duration-200 ${
                        copied ? "scale-110" : ""
                      }`}
                    />
                  </div>

                  {/* "Copied!" toast */}
                  {copied && (
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[11px] font-medium px-3 py-1.5 rounded-full whitespace-nowrap shadow-lg animate-pulse">
                      Link copied!
                    </div>
                  )}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
